/**
 * Charge-object approvers — the domain type, the DB row shape, and the pure
 * mappers / map-builders for the per-charge-object approver assignment that
 * drives charge-object approval routing (Time Approval Routing, Part 2).
 *
 * UI-free / network-free → testable, and importable by the routing engine
 * (lib/approvalRouting.ts) without dragging in the Supabase client. The service
 * (services/chargeObjectApproverService.ts) does all the I/O and re-exports.
 */
import { type SapObjectType, isSapObjectType } from './sapChargeObjects'

/** Where a charge-object approver assignment came from. */
export type ApproverSource = 'manual' | 'pm_team' | 'ai_import'

export const APPROVER_SOURCES: ApproverSource[] = ['manual', 'pm_team', 'ai_import']

export const APPROVER_SOURCE_LABEL: Record<ApproverSource, string> = {
  manual: 'Manual',
  pm_team: 'PM team',
  ai_import: 'AI import',
}

export interface ChargeObjectApprover {
  /** Synthetic id '<sapObjectType>:<objectKey>' (== sapChargeObjectId). */
  id: string
  orgId: string
  sapObjectType: SapObjectType
  objectKey: string
  approverUserId: string
  approverName: string | null
  source: ApproverSource
  assignedBy: string | null
  assignedAt: string
  updatedAt: string
}

/** Snake_case row as stored in charge_object_approvers. */
export interface DbChargeObjectApprover {
  org_id: string
  id: string
  sap_object_type: string
  object_key: string
  approver_user_id: string
  approver_name: string | null
  source: string
  assigned_by: string | null
  assigned_at: string
  updated_at: string
}

/** Coerce an arbitrary stored source string to the known union (defensive). */
export function coerceApproverSource(s: string | null | undefined): ApproverSource {
  return s === 'pm_team' || s === 'ai_import' ? s : 'manual'
}

/** Map a DB row to the app shape, coercing the type + source defensively. */
export function mapDbApprover(r: DbChargeObjectApprover): ChargeObjectApprover {
  return {
    id: r.id,
    orgId: r.org_id,
    sapObjectType: isSapObjectType(r.sap_object_type) ? r.sap_object_type : 'wbs_element',
    objectKey: r.object_key,
    approverUserId: r.approver_user_id,
    approverName: r.approver_name,
    source: coerceApproverSource(r.source),
    assignedBy: r.assigned_by,
    assignedAt: r.assigned_at,
    updatedAt: r.updated_at,
  }
}

/**
 * Index assignments by charge-object key (what a timesheet entry's charge code
 * resolves to via TsChargeCode.wbs / SapChargeObject.objectKey) — the shape the
 * routing engine consumes. On duplicate keys the last one wins.
 */
export function buildApproverMapByKey(
  approvers: ReadonlyArray<ChargeObjectApprover>,
): Map<string, ChargeObjectApprover> {
  const m = new Map<string, ChargeObjectApprover>()
  for (const a of approvers) m.set(a.objectKey, a)
  return m
}
